import moment, { Moment } from 'moment';
import { markRaw } from 'vue';
import { PLANNER_DATE_FORMAT, PLANNER_DATE_TIME_FORMAT, PLANNER_TIME_FORMAT, ROUTE_DATE_TIME_FORMAT } from '@/planner/constants/general';
import { Nullish } from '../utils/typescript';

export enum DateTimeFormat {
  DATE_TIME,
  LEGACY,
  DATE,
  PLANNER_DATE,
  PLANNER_TIME,
  PLANNER_DATE_TIME,
  ROUTE_DATE_TIME
}

/**
 * Attempts to parse a value in any format acceptable to {@link moment} and transforms it to local time.
 * The rules for transforming to local time are detailed here: https://next-apps.atlassian.net/wiki/spaces/AUT/pages/2441281565/Date+time+handling
 * If the given value is falsy, null will be returned.
 * If the given value can't be parsed to a valid moment an error is logged to the console and null is returned.
 * @param data
 * @param format whether to treat this value as DATE_TIME (default), DATE or LEGACY (cf. aforementioned documentation)
 */
export function parseMoment(data: any, format = DateTimeFormat.DATE_TIME): Moment | null {
  if (!data) return null;
  if (format === DateTimeFormat.PLANNER_DATE) {
    return moment(data, PLANNER_DATE_FORMAT);
  }
  if (format === DateTimeFormat.PLANNER_TIME) {
    return moment(data, PLANNER_TIME_FORMAT);
  }
  if (format === DateTimeFormat.ROUTE_DATE_TIME) {
    return moment(data, ROUTE_DATE_TIME_FORMAT);
  }
  const parsed = moment.parseZone(data, moment.ISO_8601);
  if (!parsed.isValid()) {
    // eslint-disable-next-line no-console
    console.error('Invalid moment value:', data);
    return null;
  }
  parsed.local();
  if (format === DateTimeFormat.DATE || format === DateTimeFormat.LEGACY) {
    parsed.subtract(parsed.utcOffset(), 'minutes');
  }
  if (format === DateTimeFormat.DATE) {
    parsed.startOf('day');
  }
  return markRaw(parsed);
}

/**
 * Inverse of {@link parseMoment}.
 * @param m a moment object or any falsy value, in which case null is returned
 * @param format
 */
export function formatMoment(m: Moment, format: DateTimeFormat): string;
export function formatMoment(m: Moment): string;
export function formatMoment(m: Nullish<Moment>, format: DateTimeFormat): string | null;
export function formatMoment(m: Nullish<Moment>): string | null;
export function formatMoment(m: Nullish<Moment>, format = DateTimeFormat.DATE_TIME): string | null {
  if (!m) {
    return null;
  } else if (format === DateTimeFormat.PLANNER_DATE) {
    return m.format(PLANNER_DATE_FORMAT);
  } else if (format === DateTimeFormat.PLANNER_TIME) {
    return m.format(PLANNER_TIME_FORMAT);
  } else if (format === DateTimeFormat.PLANNER_DATE_TIME) {
    return m.format(PLANNER_DATE_TIME_FORMAT);
  } else {
    const n = m.clone();
    if (format === DateTimeFormat.DATE || format === DateTimeFormat.LEGACY) {
      n.add(n.utcOffset(), 'minutes');
    }
    n.utc();
    if (format === DateTimeFormat.DATE) {
      n.startOf('day');
    }
    return n.format();
  }
}